/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import type { AmiGlobalRegister } from '../../api/ami';
import { NON_NUMERIC_CODES } from '../readings/series';
import type { ChartParameter, PhaseLetter, Register } from './types';

const PHASES: PhaseLetter[] = ['L1', 'L2', 'L3'];

export function toRegisters(catalogue: AmiGlobalRegister[]): Register[] {
  return catalogue
    .filter((entry) => typeof entry?.grn === 'string' && entry.grn.trim() !== '')
    .map((entry) => {
      const code = (entry.grn as string).trim();
      const label = entry.description?.trim() || code;
      return { code, label, unit: entry.unit?.trim() ?? '', order: entry.uiOrder ?? Number.MAX_SAFE_INTEGER };
    })
    .sort((a, b) => a.order - b.order || a.code.localeCompare(b.code));
}

/**
 * @param phaseTitle renames a phase parameter whose shared title is also a single parameter's label, e.g. the
 *                   per-phase "Power Factor" next to the meter's total "Power Factor"
 */
export function toChartParameters(registers: Register[], phaseTitle: (title: string) => string = (title) => title):
    ChartParameter[] {
  const grouped = new Set<string>();
  const parameters: ChartParameter[] = [];

  const ungrouped = () => registers.filter((register) => !grouped.has(register.code) && !NON_NUMERIC_CODES.has(register.code));
  const trios = detectPhaseTrios(ungrouped(), labelPhase);
  trios.flatMap((trio) => trio.members).forEach((register) => grouped.add(register.code));
  trios.push(...detectPhaseTrios(ungrouped(), obisPhase));
  for (const { id, members: trio } of trios) {
    trio.forEach((register) => grouped.add(register.code));
    const series = trio.map((register) => ({ phase: PHASES[register.phase], code: register.code, label: register.label }));
    parameters.push({
      kind: 'phase',
      id,
      title: commonTitle(series.map((entry) => entry.label)),
      unit: trio.find((member) => member.unit)?.unit ?? '',
      order: Math.min(...trio.map((member) => member.order)),
      series,
    });
  }

  for (const register of registers) {
    if (!grouped.has(register.code) && !NON_NUMERIC_CODES.has(register.code)) {
      parameters.push({ kind: 'single', code: register.code, label: register.label, unit: register.unit,
        order: register.order });
    }
  }
  return disambiguatePhaseTitles(parameters, phaseTitle).sort((a, b) => a.order - b.order);
}

function disambiguatePhaseTitles(parameters: ChartParameter[], phaseTitle: (title: string) => string): ChartParameter[] {
  const singleLabels = new Set(parameters.flatMap((parameter) =>
    parameter.kind === 'single' ? [parameter.label.toLowerCase()] : []));
  return parameters.map((parameter) =>
    parameter.kind === 'phase' && singleLabels.has(parameter.title.toLowerCase())
      ? { ...parameter, title: phaseTitle(parameter.title) }
      : parameter);
}

export function commonTitle(labels: string[]): string {
  if (labels.length === 1) {
    return labels[0];
  }
  const words = labels.map((label) => label.split(/\s+/));
  if (words.some((entry) => entry.length !== words[0].length)) {
    return labels[0];
  }
  const shared = words[0].filter((word, index) => words.every((entry) => entry[index] === word));
  return shared.length > 0 ? shared.join(' ') : labels[0];
}

interface PhaseMatch {
  key: string;
  phase: number;
}

const OBIS_CODE = /^((?:[A-Z]+_)?)(\d+)(\.\d+\.\d+.*)$/;

/**
 * OBIS puts phase L1/L2/L3 quantities 20 apart in the C field (21-40, 41-60, 61-80), so 31/51/71.7.0 are the
 * currents and 32/52/72.7.0 the voltages, under the same prefix (LP_, CO_, PQ_) and the same D.E and suffix.
 */
function obisPhase(register: Register): PhaseMatch | null {
  const match = OBIS_CODE.exec(register.code);
  const c = match ? Number(match[2]) : NaN;
  if (!match || c < 21 || c > 80) {
    return null;
  }
  const phase = Math.floor((c - 21) / 20);
  return { key: `${match[1]}${c - phase * 20}${match[3]}`, phase };
}

const PHASE_TOKEN = /(^|[\s(])([ABC])(-N)?(?=$|[\s)])/;

/**
 * Groups registers whose descriptions differ only by the phase letter, e.g. "Phase A Power factor" or
 * "Voltage A-N H3 Mag". Runs before the OBIS rule since some codes break its spacing.
 */
function labelPhase(register: Register): PhaseMatch | null {
  const match = PHASE_TOKEN.exec(register.label);
  if (!match) {
    return null;
  }
  const prefix = /^[A-Z]+_/.exec(register.code)?.[0] ?? '';
  return { key: `${prefix}${register.label.replace(PHASE_TOKEN, '$1#$3')}`, phase: 'ABC'.indexOf(match[2]) };
}

/**
 * Groups registers that share a key and unit, one per phase, into trios of at least two phases, L1 first. The trio id
 * is that shared key, so it stays the same whichever phases a reading range happens to carry.
 */
function detectPhaseTrios(registers: Register[], phaseOf: (register: Register) => PhaseMatch | null) {
  const candidates = new Map<string, (Register & { phase: number })[]>();
  for (const register of registers) {
    const match = phaseOf(register);
    if (match) {
      const key = `${match.key}|${register.unit}`;
      candidates.set(key, [...(candidates.get(key) ?? []), { ...register, phase: match.phase }]);
    }
  }
  return [...candidates.entries()]
    .filter(([, members]) => members.length >= 2 && new Set(members.map((member) => member.phase)).size === members.length)
    .map(([id, members]) => ({ id, members: members.sort((a, b) => a.phase - b.phase) }));
}
